import filter from 'lodash/filter'
import map from 'lodash/map'
import { NextPage } from 'next'
import Head from 'next/head'
import React, { useMemo } from 'react'
import { SWRConfig } from 'swr'

import { contentstackLoader } from '@Components/Image'
import { BlockConfigContext } from '@Globals/CMS/Context'
import { DataLayer } from '@Globals/Google/DataLayer'
import locales from '@Globals/Locales/locales.json'
import { usePageCanonical } from '@Globals/Page/Canonical'
import { PageProps } from '@Globals/Page/Types'

const DynamicHead = ({
  alternateLinks = [],
  blockConfig,
  openGraph,
  page,
  seo,
}: PageProps) => {
  const filteredAlternateLinks = useMemo(() => filter(
    alternateLinks,
    (link) => {
      if (locales.includes(link.locale) === false) {
        return false
      }

      return true
    },
  ), [alternateLinks])

  const { titleAfter, titleBefore, url: pageURL, urlExtra: pageURLExtra } = usePageCanonical(page, blockConfig)

  return (
    <Head>
      {/* SEO */}
      {seo?.title && <title>{[...titleBefore, seo.title, ...titleAfter].join(' - ')}</title>}
      {seo?.description && <meta key="description" content={seo.description} name="description" />}
      <meta key="robots" content={seo?.noIndex === true ? 'noindex, follow' : 'index, follow'} name="robots" />
      {pageURL && <link key="canonical" href={pageURL} rel="canonical" />}

      {/* Open Graph */}
      {openGraph?.description && <meta key="og:description" content={openGraph.description} property="og:description" />}
      {openGraph?.image && <meta key="og:image" content={contentstackLoader({ src: openGraph.image, width: 1200 })} property="og:image" />}
      {openGraph?.siteName && <meta key="og:site_name" content={openGraph.siteName} property="og:site_name" />}
      {openGraph?.title && <meta key="og:title" content={[...titleBefore, openGraph.title, ...titleAfter].join(' - ')} property="og:title" />}
      {openGraph?.type && <meta key="og:type" content={openGraph.type} property="og:type" />}
      {page.url && <meta key="og:url" content={pageURL} property="og:url" />}

      {/* Alternate Links */}
      {map(filteredAlternateLinks, item => (
        <link
          key={item.locale}
          href={`${item.url || ''}${pageURLExtra}`}
          hrefLang={item.locale}
          rel="alternate"
        />
      ))}
    </Head>
  )
}

export const PageHOC = <T extends PageProps>(Component: NextPage<T>): NextPage<T> => (props) => {
  const {
    blockConfig,
    page,
    swrFallback,
  } = props

  return (
    <>
      <DynamicHead {...props} />

      {/* Render component */}
      <SWRConfig value={{ fallback: swrFallback }}>
        <BlockConfigContext.Provider value={blockConfig}>
          <Component {...props} />
        </BlockConfigContext.Provider>
      </SWRConfig>

      {/* Tracking */}
      <DataLayer input={{ contentIds: page.id, pageType: page.type, type: page.type }} />
    </>
  )
}
